import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useCallback, useMemo } from 'react';
import { ContactApi } from '../../../services/api-client/contact-api';
import CallHistoryService from '../../../services/call-history/call-history-service';
import { CommunicationType } from '../../../services/inbox-service/types';
import LegacyMessengerService from '../../../services/legacy-message/legacy-messenger-service';
import { IMailboxWithLocation } from '../../../services/mailbox-service/mailbox-service';
import { Message } from '../../../services/pubnub-service';
import { TemplateTransportType } from '../../../types/communication/transport-type';
import { PatientCaseWithTimetoken } from '../../use-leads-management/use-leads-management';
import { usePubnub } from '../../use-pubnub/use-pubnub';
import { CallHistoryItem, transformPayloadToCallHistoryItem } from '../types/comms-call-types';
import { ManagedCommItem } from '../types/managed-communication';
import { CommunicationDataRequest, LegacySearchParams } from './types';

const DEFAULT_PAGE_ITEM_COUNT = 25;

export interface CommunicationDataContext {
  getMessageHistory: (
    request: CommunicationDataRequest,
    loadedData: (Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken)[]
  ) => Promise<{ messages: Message[]; legacySearchParams: LegacySearchParams }>;
  getCallHistory: (request: CommunicationDataRequest) => Promise<CallHistoryItem[]>;
  getManagedCommunicationsHistory: (request: CommunicationDataRequest) => Promise<ManagedCommItem[]>;
}

export const useCommuicationData = (): CommunicationDataContext => {
  const { config } = useConfig();
  const { client } = usePubnub();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const callHistoryApi = useMemo(() => CallHistoryService.getInstance(config, oidcService), [config, oidcService]);
  const legacyClient = useMemo(() => LegacyMessengerService.getInstance(config, oidcService), [config, oidcService]);

  const performPubnubSearch = useCallback(
    async (channelIds: string[], start: string | undefined) => {
      if (!client) return;

      const messageResponses = await Promise.all(channelIds.map((id) => client.fetchMessageHistory(id, start)));
      return messageResponses.reduce((acc, res) => [...acc, ...res], []);
    },
    [client]
  );

  const performLegacySearch = useCallback(
    async (phoneNumber: string, currMailboxes: IMailboxWithLocation[], pageNumber: number) => {
      if (!legacyClient) return;

      const legacyMessagesRequest = currMailboxes.map((currMailbox) => {
        const locationId = currMailbox.locationId.toString();
        return legacyClient.fetchMessageHistory(
          phoneNumber,
          locationId,
          {
            pageNumber: pageNumber,
            pageSize: DEFAULT_PAGE_ITEM_COUNT
          },
          currMailbox
        );
      });

      const legacyMessageResponses = await Promise.all(legacyMessagesRequest);
      return legacyMessageResponses.reduce((acc, res) => [...acc, ...res], []);
    },
    [legacyClient]
  );

  const performCallHistorySearch = useCallback(
    async (locationIds: string[], phoneNumber: string, start: string | undefined) => {
      if (!callHistoryApi) return;
      const callHistoryResponse = await callHistoryApi.search({
        searchText: phoneNumber,
        locationIds: locationIds,
        timeStartTo: start ? new Date(parseFloat(start)).toISOString() : undefined,
        pageSize: DEFAULT_PAGE_ITEM_COUNT
      });

      return callHistoryResponse.map((item) => transformPayloadToCallHistoryItem(item));
    },
    [callHistoryApi]
  );

  const getMessageHistory = useCallback(
    async (
      request: CommunicationDataRequest,
      loadedData: (Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken)[]
    ) => {
      const { phoneNumbers, mailboxes } = request.query;
      const { pubnubComplete, legacyComplete, legacyData, pageNumber } = request.legacySearchParams;

      if (!phoneNumbers || !mailboxes) return { messages: [], legacySearchParams: request.legacySearchParams };

      const channelIds = phoneNumbers.flatMap((phoneNumber) =>
        mailboxes.map((item) => `${item.pubnubPrefix}.${phoneNumber}`)
      );
      const locationIds = mailboxes.map((item) => item.locationId.toString()) || [];

      let messageData: Message[] = [];

      // search message history
      if (!pubnubComplete && channelIds?.length)
        messageData = (await performPubnubSearch(channelIds, request.start)) || [];

      const isPubnubComplete = pubnubComplete || messageData.length < DEFAULT_PAGE_ITEM_COUNT;
      const isLegacySearch = !legacyComplete && isPubnubComplete;

      // search legacy message history if message history is complete
      let legacyMessageData: Message[] = [];
      let page = pageNumber;

      if (isLegacySearch && locationIds?.length) {
        const remainingLegacyData = legacyData.filter((legacyMessage) => !loadedData.includes(legacyMessage));
        messageData = [...remainingLegacyData, ...messageData];

        if (remainingLegacyData.length < DEFAULT_PAGE_ITEM_COUNT) {
          legacyMessageData =
            (
              await Promise.all(
                phoneNumbers.map((phoneNumber) =>
                  performLegacySearch(phoneNumber, mailboxes, pageNumber)
                    .then((msgs) => msgs)
                    .catch((err) => {
                      console.error(err);
                      const msgs: Message[] = [];
                      return msgs;
                    })
                )
              )
            ).flatMap((x) => (x ? x : [])) || [];
          messageData = [...legacyMessageData, ...messageData];
          page = page + 1;
        }
      }

      return {
        messages: messageData,
        legacySearchParams: {
          pubnubComplete: isPubnubComplete,
          legacyComplete: legacyMessageData.length < DEFAULT_PAGE_ITEM_COUNT,
          pageNumber: page,
          legacyData: [...legacyMessageData, ...legacyData]
        }
      };
    },
    [performPubnubSearch, performLegacySearch]
  );

  const getCallHistory = useCallback(
    async (request: CommunicationDataRequest) => {
      const { phoneNumbers, mailboxes, patientId } = request.query;

      if ((!phoneNumbers || !mailboxes) && !patientId) return [];

      const locationIds = mailboxes?.map((item) => item.locationId.toString()) || [];

      const allPhoneNumbers = phoneNumbers || [];

      const callData = (
        await Promise.all(
          allPhoneNumbers.map((phoneNumber) => performCallHistorySearch(locationIds, phoneNumber, request.start))
        )
      ).flatMap((x) => (x ? x : []));
      return callData.reduce<CallHistoryItem[]>((acc, obj) => {
        if (!acc.some((item) => item.id === obj.id)) {
          acc.push(obj);
        }
        return acc;
      }, []);
    },
    [performCallHistorySearch]
  );

  const getManagedCommunicationsHistory = useCallback(async (request: CommunicationDataRequest) => {
    const { phoneNumbers, commType, mailboxes, patientId } = request.query;

    if ((!phoneNumbers || !mailboxes) && !patientId) return [];

    const claims = await getClaims();
    const contactApi = ContactApi.getInstance();

    const payload = {
      phoneNumbers: phoneNumbers || [],
      patientId: patientId || undefined,
      startDate: request.start ? new Date(parseFloat(request.start)) : undefined,
      pageSize: DEFAULT_PAGE_ITEM_COUNT,
      transportType:
        commType === CommunicationType.Email
          ? TemplateTransportType.Email
          : commType === CommunicationType.Sms
          ? TemplateTransportType.Sms
          : undefined
    };

    return (await contactApi.getInboxManagedCommunications(claims.locationId, payload)) as ManagedCommItem[];
  }, []);

  return {
    getMessageHistory,
    getCallHistory,
    getManagedCommunicationsHistory
  };
};
